import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { assessmentService } from '../services/assessmentService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Loader2, Mail, Lock, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email:
      location.state?.prefilledEmail ||
      location.state?.pendingResultsLink?.email ||
      location.state?.pendingAssessmentSubmission?.submissionPayload?.email ||
      '',
    password: ''
  });
  const [errors, setErrors] = useState({});

  const redirectTo = location.state?.from?.pathname || '/dashboard';
  const pendingAssessmentSubmission = location.state?.pendingAssessmentSubmission || null;
  const pendingResultsLink = location.state?.pendingResultsLink || null;
  const backTo = location.state?.backTo || '/';
  const backState = location.state?.backState || undefined;

  const handleBack = () => {
    navigate(backTo, backState ? { state: backState } : undefined);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const loginResponse = await login(formData);

      if (pendingAssessmentSubmission?.submissionPayload) {
        const result = await assessmentService.submitCompletion(
          pendingAssessmentSubmission.submissionPayload
        );

        toast.success('Welcome back!');

        navigate('/assessment/results', {
          replace: true,
          state: {
            result,
            email: pendingAssessmentSubmission.submissionPayload.email,
            createdAccount: false,
            language: pendingAssessmentSubmission.submissionPayload.language,
            userName: loginResponse?.user?.first_name || ''
          }
        });

        return;
      }

      if (pendingResultsLink?.completion_id) {
        await assessmentService.linkCompletionToAccount(pendingResultsLink.completion_id);

        toast.success('Welcome back!');

        navigate('/assessment/results', {
          replace: true,
          state: {
            result: pendingResultsLink.result,
            email: pendingResultsLink.email,
            createdAccount: true,
            language: pendingResultsLink.language,
            userName: loginResponse?.user?.first_name || ''
          }
        });

        return;
      }

      toast.success('Welcome back!');
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (pendingAssessmentSubmission?.submissionPayload && error.response?.status !== 401) {
        toast.error('Login worked, but we could not finish saving your results. Please try again.');
        navigate('/assessment/register', {
          state: backState
        });
      } else if (pendingResultsLink?.completion_id && error.response?.status !== 401) {
        toast.error('Login worked, but we could not connect this result to your account. Your result is still available on this page.');
        navigate('/assessment/results', {
          replace: true,
          state: {
            result: pendingResultsLink.result,
            email: pendingResultsLink.email,
            createdAccount: false,
            language: pendingResultsLink.language,
            userName: pendingResultsLink.userName || ''
          }
        });
      } else {
        toast.error(error.response?.data?.detail || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-brand-50/20 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={handleBack}
          className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-brand-600 transition-colors"
          data-testid="login-back-btn"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>

        <Card className="w-full shadow-xl border-gray-200">
          <CardHeader className="space-y-3 bg-gradient-to-br from-brand-50 to-white border-b border-gray-200">
            <div className="flex justify-center">
              <img src="/spc-logo.png" alt="SPC Logo" className="w-16 h-16 object-contain" />
            </div>
            <CardTitle className="text-2xl font-black text-center text-gray-900">
              Sign In
            </CardTitle>
            <CardDescription className="text-center">
              Access your well-being dashboard
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="your.email@example.com"
                    data-testid="login-email-input"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`pl-10 ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">{errors.password}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-600 hover:bg-brand-700 py-6"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/" className="text-brand-600 hover:underline font-semibold">
                  Take the assessment
                </Link>
              </p>
              <p className="text-xs text-gray-500">
                Create an account after completing the assessment
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
